import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, inject} from '@angular/core';
import {
  addresses,
  creditFreezeMessage,
  formatGivenNames,
  getFullNameFromConsumerRequest,
  hasHrfa,
  hrfa,
  isCreditFreezeAndIsNonExemptedMC,
  isCreditFreezeMessageExists,
  isHit,
  isHrfaClear,
  names,
  primaryGivenNames,
  primaryName,
  primaryPhone
} from 'src/app/_utils/consumer';
import {
  Address,
  Collection,
  Consumer,
  ConsumerReport,
  CreditSummaryDetail,
  EmploymentAddress,
  Mortgage,
  Name,
  RegisteredItem,
  Score,
  Trade
} from '../../../../_data/reports/consumer/consumer-report';
import {ConsumerRequest} from '../../../../_data/reports/consumer/consumer-request';
import {dayMonthYear, equalsIgnoreCase, isBlank, isNotBlank, kob, maskSin, reformatDate, reformatPostal, removeExtraWhitespace} from 'src/app/_utils/lang';
import {Observable, of} from 'rxjs';
import {isIdReport, isIntactReport} from '../../../../_utils/products';
import {I18nService} from '../../../../_services/gui/i18n.service';
import {switchMap} from 'rxjs/operators';
import {CREDIT_SUMMARY_DETAIL_ORDER} from '../../../../_constants/constants';
import {isNotEmpty} from '../../../../_utils/collect';
import {NgClass, AsyncPipe, KeyValuePipe} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';

@Component({
    selector: 'app-consumer-credit-report-content',
    templateUrl: './consumer-credit-report-content.component.html',
    styleUrls: ['./consumer-credit-report-content.component.scss'],
    imports: [NgClass, AsyncPipe, KeyValuePipe, TranslateModule]
})
export class ConsumerCreditReportContentComponent implements OnInit {

  @Input() consumerReport: ConsumerReport;

  @Input() consumerRequest: ConsumerRequest;
  @Output() creditReport: EventEmitter<ElementRef> = new EventEmitter();

  i18nService = inject(I18nService);

  reformatDate = reformatDate;
  dayMonthYear = dayMonthYear;
  reformatPostal = reformatPostal;
  kob = kob;
  maskSin = maskSin;
  names = names;
  primaryName = primaryName;
  primaryGivenNames = primaryGivenNames;
  addresses = addresses;
  primaryPhone = primaryPhone;
  hrfa = hrfa;
  hasHrfa = hasHrfa;
  isHrfaClear = isHrfaClear;
  isHit = isHit;
  isCreditFreezeMessageExists = isCreditFreezeMessageExists;
  isCreditFreezeAndIsNonExemptedMC = isCreditFreezeAndIsNonExemptedMC;
  formatGivenNames = formatGivenNames;
  creditFreezeMessage = creditFreezeMessage;
  getFullNameFromConsumerRequest = getFullNameFromConsumerRequest;

  private readonly typeCountMap = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    R: 'numRevolvingTrades', O: 'openTradeAccounts', I: 'numInstallmentTrades', M: 'numMortgageTrades', C: 'numCollections'
  };

  ngOnInit(): void {
    // Put the creditSummaryDetail array into the same order as legacy.
    this.consumerReport.consumers.forEach(consumer => {
      consumer.creditSummaryDetail.sort(
        (a: CreditSummaryDetail, b: CreditSummaryDetail) => CREDIT_SUMMARY_DETAIL_ORDER.indexOf(a.summaryType) - CREDIT_SUMMARY_DETAIL_ORDER.indexOf(b.summaryType)
      );
    });
  }

  monthYear(date: string): string {
    return reformatDate(date, 'MMMYYYY');
  }

  formatCurrency(s: string | number): Observable<string> {
    return this.i18nService.formatCurrency(s);
  }

  summaryCountDate(count, date): string {
    const formattedDate = this.monthYear(date);
    return `${count}${formattedDate ? '-' + formattedDate : ''}`;
  }

  secondaryNames(consumer: Consumer): Name[] {
    return names(consumer).filter(n => n.nameIndicator === '3') || [];
  }

  primaryAddress(consumer: Consumer): string {
    const address = consumer.addresses.find(a => a.addressQualifier === 'H');
    if(address) {
      const n = address.houseNumber || '';
      const s = address.streetName && `${address.streetName},` || '';
      const c = address.city && `${address.city},` || '';
      const p = address.province && `${address.province},` || '';
      const po = address.postal || '';
      return removeExtraWhitespace(`${n} ${s} ${c} ${p} ${po}`);
    }
    return '';
  }

  civicStreet(address: Address) {
    return (isBlank(address.houseNumber) ? '' : `${address.houseNumber} `) + address.streetName + (isBlank(address.aptUnit) ? '' : ` ${address.aptUnit}`);
  }

  prevPhone(consumer: Consumer): string {
    const phones = consumer.phones?.filter(p => p.phoneTypeIndicator === 'H');
    return phones?.[1]?.telephoneNumber ?? '';
  }

  formatEmployerNameAddress(employerName: string, employmentAddress: EmploymentAddress ): string {
    return [employerName, employmentAddress?.city, employmentAddress?.province].filter(v => !isBlank(v)).join(', ');    
  }

  aacList(s: Score): [string, string][] {
    return [
      [s.aacCode1, s.aacCodeText1],
      [s.aacCode2, s.aacCodeText2],
      [s.aacCode3, s.aacCodeText3],
      [s.aacCode4, s.aacCodeText4]
    ];
  }

  isProductAddonCode703(s: Score): boolean {
    return equalsIgnoreCase('00703', s.productAddonCode);
  }

  formatMop(v: Trade | Mortgage) {
    return `${v.times30 || '0'} ${v.times60 || '0'} ${v.times90 || '0'} ${v.monthsReviewed || '0'}`;
  }

  formatIdxIdentifier(consumer: Consumer): Observable<string> {
    return of(consumer.idxUniqueIdentifier.idxIdentifier).pipe(
      switchMap(v => isBlank(v) ? this.i18nService.repeatOnLangChange(() => this.i18nService.getTranslation('content.blank')) : of(v))
    );
  }

  formatSubscriber(r: RegisteredItem) {
    return `${r.subscriberName}/${r.subscriberTelephone}`;
  }

  formatRegisteredItemNarrativeCodes(r: RegisteredItem) {
    return `${r.narrativeCode1},${r.narrativeCode2}`;
  }

  formatCollectionStatus(c: Collection) {
    if(c.status === 'PD') {
      return `${c.collectionStatusText} ${this.monthYear(c.datePaidOut)}`;
    } else if(isNotBlank(c.status)) {
      return c.collectionStatusText;
    }
    return null;
  }

  formatCollectionNarratives(c: Collection) {
    return [c.ownerIndicText, c.narr1Text, c.narr2Text].filter(v => !!v).join(', ');
  }

  formatPaymentPattern(item: Trade | Mortgage): string {
    return item.paymentPattern.substring(0, 24);
  }

  formatCreditAvailable(csd: CreditSummaryDetail): number {
    const totalCredLimitRev = parseInt(csd.creditLimit, 10);
    const totalBalanceRev = parseInt(csd.balance, 10);
    if(totalCredLimitRev === 0) {
       return 0;
    } else {
      return (totalCredLimitRev - totalBalanceRev) / totalCredLimitRev;
    }
  }

  highCreditOrCreditLimit(item: Trade | Mortgage): Observable<string> {
    let hCredit: number;
    if(equalsIgnoreCase('H', item.bestAmtFlag)) {
      hCredit = item.highCredit;
    } else if(equalsIgnoreCase('C', item.bestAmtFlag)) {
      hCredit = item.creditLimit;
    } else {
      hCredit = item.highCredit && item.highCredit > 0 ? item.highCredit : item.creditLimit;
    }
    return this.formatCurrency(hCredit);
  }

  getCreditSummaryDetailCount(consumer: Consumer, csd: CreditSummaryDetail): number {
    return Number(consumer?.creditSummary?.[this.typeCountMap?.[csd.summaryType]] ?? 0);
  }

  getTotalCreditSummaryDetailCount(consumer: Consumer) {
    return consumer.creditSummaryDetail.map(csd => this.getCreditSummaryDetailCount(consumer, csd)).reduce((total, num) => total + num);
  }

  getNegativeTrades(consumer: Consumer): number {
    const negativeTrades = parseInt(consumer.creditSummary.numNegativeTrades ?? '0', 10);
    const badMop = parseInt(consumer.creditSummary2.trBadMop ?? '0', 10);
    return Math.max(negativeTrades, badMop);
  }

  getCreditFreezeDate(consumer: Consumer): string | '' {
    const freezeMessage = creditFreezeMessage(consumer);
    if(freezeMessage !== '' && freezeMessage !== undefined && freezeMessage.length > 0) {
      return dayMonthYear(freezeMessage.substring(freezeMessage.length-10,freezeMessage.length),'DDMMYYYY');
    }
  }

  hasRemarks(consumer: Consumer): boolean {
    return consumer?.miscStatements?.length > 0 || consumer?.consumerStatement?.length > 0;
  }

  remarks(consumer: Consumer): {message: string; date: string; type: string}[] {
    return [
      ...consumer?.miscStatements?.map(ms => ({message: ms.information, date: ms.remarkDate, type: ms.remarkType})),
      ...consumer?.consumerStatement?.map(ms => ({message: ms.statementText, date: ms.remarkDate, type: ms.remarkType}))
    ];
  }

  isNoHit(consumer: Consumer): boolean {
    return consumer?.subject?.hit === 'N';
  }

  hasScotiaTrades(consumer: Consumer): boolean {
    return consumer.scotiaTrades?.length > 0;
  }

  /** The scotiaTrades json object should only exist when we're supposed to use it instead of the normal trades object */
  getTrades(consumer: Consumer): Trade[] {
    return consumer.scotiaTrades?.length > 0 ? consumer.scotiaTrades : consumer.trades;
  }

  @ViewChild('creditReport', {read: ElementRef})
  set _creditReport(value: ElementRef) {
    this.creditReport.emit(value);
  }

  get displayFileSummary(): boolean {
    // TODO: should be able to just check if the creditSummary2 section is empty for the Intact report.
    return !isIdReport(this.consumerRequest?.metaInfo?.product) && !isIntactReport(this.consumerRequest?.metaInfo?.product);
  }

  formatNarrativeText(text1: any, text2: any) {
    if(isNotEmpty(text2) && isNotEmpty(text1)) {
      return text1.concat(', ').concat(text2);
    } else if(isNotEmpty(text1)) {
      return text1;
    }
  }
}
